import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@context/AuthContext';
import { useTheme } from '@context/ThemeContext';
import { ThemeSelector } from '@components/ui/ThemeSelector';
import { ProfileSettingsModal } from '@components/ui/ProfileSettingsModal';
import { HelpChatbot } from '@components/ui/HelpChatbot';
import { SupportTicketModal } from '@components/ui/SupportTicketModal';
import { NotificationBell } from '@components/ui/NotificationBell';
import { PlanBadge } from '@components/ui/PlanBadge';

interface HeaderProps {
  userName?: string;
  userEmail?: string;
  userAvatar?: string;
}

export const Header: React.FC<HeaderProps> = ({
  userName = 'User',
  userEmail = 'user@example.com',
  userAvatar = '👤',
}) => {
  const navigate = useNavigate();
  const { logout, user: authUser, updateProfile } = useAuth();
  const { themeConfig } = useTheme();
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isProfileSettingsOpen, setIsProfileSettingsOpen] = useState(false);
  const [isHelpChatbotOpen, setIsHelpChatbotOpen] = useState(false);
  const [isSupportTicketOpen, setIsSupportTicketOpen] = useState(false);
  const [escalateContext, setEscalateContext] = useState<{ sessionId?: string; message?: string }>({});
  const profileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setIsProfileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleProfileMenuClick = (itemId: string) => {
    if (itemId === 'my-profile')  navigate('/my-profile');
    if (itemId === 'logout')      { logout(); navigate('/auth/login'); }
    if (itemId === 'account')     setIsProfileSettingsOpen(true);
    if (itemId === 'help')        setIsHelpChatbotOpen(true);
    setIsProfileMenuOpen(false);
  };

  const handleProfileSave = async (profileData: any) => {
    if (!authUser?.id) throw new Error('User not authenticated');
    const updateData: any = {
      first_name:      profileData.firstName   || undefined,
      last_name:       profileData.lastName    || undefined,
      phone_number:    profileData.phone       || undefined,
      gender:          profileData.gender      || undefined,
      date_of_birth:   profileData.dob         || undefined,
      profile_image:   profileData.profilePic  || undefined,
      address:         profileData.address     || undefined,
      city:            profileData.city        || undefined,
      state:           profileData.state       || undefined,
      country:         profileData.country     || undefined,
      pincode:         profileData.zipCode     || undefined,
      class_or_standard: profileData.classOrStandard || undefined,
    };
    if (profileData.newPassword) updateData.password = profileData.newPassword;
    Object.keys(updateData).forEach(k => { if (updateData[k] === undefined) delete updateData[k]; });
    // updateProfile (not authService.updateUser directly) so the header's own name/avatar --
    // derived from useAuth().user in MainLayout -- refreshes immediately instead of only after
    // a reload re-reads localStorage.
    await updateProfile(authUser.id, updateData);
  };

  // Initials from name for avatar fallback
  const initials = userName
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <header className={`${themeConfig.header.background} border-b ${themeConfig.header.border} sticky top-0 z-50`}
            style={{ boxShadow: '0 1px 0 rgba(0,0,0,0.15)' }}>
      <div className="flex items-center justify-between px-3 md:px-6 h-14 gap-2 md:gap-4">

        {/* ── Logo ─────────────────────────────────────────── */}
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="text-2xl leading-none flex-shrink-0">🎓</span>
          <div className="flex flex-col leading-none min-w-0">
            <span className={`text-sm font-semibold tracking-tight truncate ${themeConfig.header.logo}`}>
              Aahana <span className="font-normal opacity-30">|</span> SmartTutor
            </span>
            <span className={`hidden sm:block text-[8px] font-medium tracking-[0.22em] uppercase ${themeConfig.header.logo} opacity-35`}>
              Limitless · Simple · Mastery
            </span>
          </div>
        </div>

        {/* ── Right controls ───────────────────────────────── */}
        <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">

          {/* Plan / trial badge */}
          <PlanBadge onClick={() => navigate('/pricing')} />

          {/* Theme Selector */}
          <ThemeSelector />

          {/* Notifications */}
          <div className={themeConfig.header.iconColor}>
            <NotificationBell buttonClassName={themeConfig.header.iconHover} />
          </div>

          {/* ── Profile Dropdown ─────────────────────────────── */}
          <div ref={profileRef} className="relative">
            <button
              onClick={() => setIsProfileMenuOpen(v => !v)}
              className={`flex items-center gap-1.5 sm:gap-2 pl-1.5 sm:pl-2 pr-1.5 sm:pr-3 py-1.5 rounded-lg transition-colors ${themeConfig.header.iconHover}`}
            >
              {/* Avatar circle */}
              <div className={`w-7 h-7 ${themeConfig.accent.primary} text-white rounded-full flex items-center justify-center font-bold text-xs flex-shrink-0 ring-2 ring-amber-400/30 overflow-hidden`}>
                {/^(https?:|data:|\/)/.test(userAvatar)
                  ? <img src={userAvatar} alt={userName} className="w-full h-full object-cover" />
                  : userAvatar.length > 2 ? initials : userAvatar}
              </div>
              <span className={`hidden sm:block text-xs font-medium ${themeConfig.header.text} max-w-[90px] truncate`}>
                {userName}
              </span>
              <svg className={`w-3.5 h-3.5 ${themeConfig.header.iconColor} transition-transform ${isProfileMenuOpen ? 'rotate-180' : ''}`}
                   fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {isProfileMenuOpen && (
              <div
                onMouseLeave={() => setIsProfileMenuOpen(false)}
                className={`absolute right-0 mt-2 w-56 rounded-xl border z-50 overflow-hidden
                            ${themeConfig.dropdown.background}
                            ${themeConfig.dropdown.border}
                            ${themeConfig.dropdown.shadow}`}
              >
                {/* User info */}
                <div className={`px-4 py-3 border-b ${themeConfig.dropdown.headerBg} ${themeConfig.dropdown.divider}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 ${themeConfig.accent.primary} text-white rounded-full flex items-center justify-center font-bold text-sm ring-2 ring-amber-400/25`}>
                      {initials}
                    </div>
                    <div className="min-w-0">
                      <p className={`text-sm font-semibold ${themeConfig.dropdown.text} truncate`}>{userName}</p>
                      <p className={`text-xs ${themeConfig.dropdown.textSecondary} truncate`}>{userEmail}</p>
                    </div>
                  </div>
                </div>

                {/* Menu items */}
                <div className="p-1.5 space-y-0.5">
                  {[
                    { id: 'my-profile', label: 'My Profile',        icon: '👤' },
                    { id: 'account',    label: 'Account Settings',  icon: '⚙️' },
                    { id: 'help',       label: 'Help & Support',    icon: '❓' },
                  ].map(item => (
                    <button
                      key={item.id}
                      onClick={() => handleProfileMenuClick(item.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors
                                  ${themeConfig.dropdown.text} ${themeConfig.dropdown.hoverBg} ${themeConfig.dropdown.hoverText}`}
                    >
                      <span className="text-base">{item.icon}</span>
                      <span>{item.label}</span>
                    </button>
                  ))}
                </div>

                {/* Logout */}
                <div className={`p-1.5 border-t ${themeConfig.dropdown.divider}`}>
                  <button
                    onClick={() => handleProfileMenuClick('logout')}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm
                               text-red-400 hover:bg-red-500 hover:text-white transition-colors"
                  >
                    <span className="text-base">🚪</span>
                    <span className="font-medium">Logout</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      <ProfileSettingsModal
        isOpen={isProfileSettingsOpen}
        onClose={() => setIsProfileSettingsOpen(false)}
        onSave={handleProfileSave}
        initialData={{
          username:        authUser?.username         || userName.split(' ')[0].toLowerCase(),
          email:           authUser?.email            || userEmail,
          phone:           authUser?.phone_number     || '',
          firstName:       authUser?.first_name       || userName.split(' ')[0],
          lastName:        authUser?.last_name        || userName.split(' ')[1] || '',
          profilePic:      authUser?.profile_image    || '',
          dob:             authUser?.date_of_birth    || '',
          gender:          authUser?.gender           || '',
          address:         authUser?.address          || '',
          city:            authUser?.city             || '',
          state:           authUser?.state            || '',
          zipCode:         authUser?.pincode          || '',
          country:         authUser?.country          || '',
          classOrStandard: authUser?.class_or_standard || '',
        }}
      />
      <HelpChatbot
        isOpen={isHelpChatbotOpen}
        onClose={() => setIsHelpChatbotOpen(false)}
        onEscalate={(context) => { setEscalateContext(context); setIsHelpChatbotOpen(false); setIsSupportTicketOpen(true); }}
      />
      <SupportTicketModal
        isOpen={isSupportTicketOpen}
        onClose={() => setIsSupportTicketOpen(false)}
        previousMessage={escalateContext.message}
        aiSessionId={escalateContext.sessionId}
      />
    </header>
  );
};
